// src/components/AdminFeedbackPanel.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { feedbackService } from '../services/feedbackService';

const AdminFeedbackPanel = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  useEffect(() => {
    loadAllFeedbacks();
    loadFeedbackStats();
  }, []);

  const loadAllFeedbacks = async () => {
    setLoading(true);
    try {
      const result = await feedbackService.getAllFeedbacks();
      if (result.success) {
        setFeedbacks(result.feedbacks || []);
      } else {
        console.error('Failed to load feedbacks:', result.message);
        setFeedbacks([]);
      }
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFeedbackStats = async () => {
    try {
      const result = await feedbackService.getFeedbackStats();
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleUpdateStatus = async (feedbackId, status, adminResponse = '') => {
    setUpdating(true);
    try {
      const result = await feedbackService.updateFeedbackStatus(feedbackId, status, adminResponse);
      if (result.success) {
        await loadAllFeedbacks();
        await loadFeedbackStats();
        setShowResponseModal(false);
        setSelectedFeedback(null);
        setResponseText('');
        setSelectedStatus('');
      } else {
        alert(result.message || 'Failed to update feedback');
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert('Failed to update feedback');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }
    setUpdating(true);
    try {
      const result = await feedbackService.deleteFeedback(feedbackId);
      if (result.success) {
        await loadAllFeedbacks();
        await loadFeedbackStats();
      } else {
        alert(result.message || 'Failed to delete feedback');
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('Failed to delete feedback');
    } finally {
      setUpdating(false);
    }
  };

  const openResponseModal = (feedback) => {
    setSelectedFeedback(feedback);
    setResponseText(feedback.admin_response || '');
    setSelectedStatus(feedback.status || 'pending');
    setShowResponseModal(true);
  };

  const handleSubmitResponse = async () => {
    await handleUpdateStatus(selectedFeedback.id, selectedStatus, responseText);
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending', icon: 'fa-clock', color: '#f59e0b', bg: '#fef3c7' },
      reviewed: { label: 'Reviewed', icon: 'fa-eye', color: '#3b82f6', bg: '#dbeafe' },
      responded: { label: 'Responded', icon: 'fa-reply', color: '#10b981', bg: '#d1fae5' },
      resolved: { label: 'Resolved', icon: 'fa-check-circle', color: '#059669', bg: '#a7f3d0' }
    };
    return configs[status] || configs.pending;
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getCategoryConfig = (category) => {
    const configs = {
      general: { label: 'General', icon: 'fa-comment', color: '#6b7280' },
      facilities: { label: 'Facilities', icon: 'fa-building', color: '#3b82f6' },
      events: { label: 'Events', icon: 'fa-calendar-alt', color: '#8b5cf6' },
      booking: { label: 'Booking', icon: 'fa-calendar-check', color: '#10b981' },
      staff: { label: 'Staff', icon: 'fa-user-tie', color: '#f59e0b' },
      suggestion: { label: 'Suggestion', icon: 'fa-lightbulb', color: '#ec4899' },
      complaint: { label: 'Complaint', icon: 'fa-exclamation-triangle', color: '#ef4444' }
    };
    return configs[category] || configs.general;
  };

  // Filter feedbacks
  const filteredFeedbacks = feedbacks.filter(fb => {
    if (statusFilter !== 'all' && fb.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && fb.category !== categoryFilter) return false;
    if (ratingFilter !== 'all' && fb.rating !== parseInt(ratingFilter)) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = 
        fb.member_name?.toLowerCase().includes(term) ||
        fb.member_email?.toLowerCase().includes(term) ||
        fb.comment?.toLowerCase().includes(term);
      if (!matches) return false;
    }
    return true;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFeedbacks = filteredFeedbacks.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFeedbacks.length / itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  if (loading) {
    return (
      <div className="admin-feedback-loading">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="loading-spinner"></div>
          <p>Loading feedback data...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="admin-feedback-container">
      {/* Header Section */}
      <motion.div 
        className="feedback-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-comment-dots"></i>
          </div>
          <div className="header-text">
            <h1>Feedback Management</h1>
            <p>Review, respond, and manage member feedback</p>
          </div>
        </div>
        <motion.button 
          className="refresh-btn"
          onClick={loadAllFeedbacks}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <i className="fas fa-sync-alt"></i> Refresh
        </motion.button>
      </motion.div>

      {/* Stats Cards */}
      {stats && (
        <motion.div 
          className="stats-grid"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="stat-card" variants={fadeInUp}>
            <div className="stat-icon total">
              <i className="fas fa-inbox"></i>
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.total || 0}</div>
              <div className="stat-label">Total Feedback</div>
            </div>
          </motion.div>
          
          <motion.div className="stat-card pending" variants={fadeInUp}>
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.pending || 0}</div>
              <div className="stat-label">Pending Review</div>
            </div>
          </motion.div>
          
          <motion.div className="stat-card responded" variants={fadeInUp}>
            <div className="stat-icon">
              <i className="fas fa-reply"></i>
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.responded || 0}</div>
              <div className="stat-label">Responded</div>
            </div>
          </motion.div>
          
          <motion.div className="stat-card resolved" variants={fadeInUp}>
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.resolved || 0}</div>
              <div className="stat-label">Resolved</div>
            </div>
          </motion.div>
          
          <motion.div className="stat-card rating" variants={fadeInUp}>
            <div className="stat-icon">
              <i className="fas fa-star"></i>
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.avg_rating || 0}<span className="small">/5</span></div>
              <div className="stat-label">Average Rating</div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Filters Section */}
      <motion.div 
        className="filters-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="search-wrapper">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by member name, email, or comment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="responded">Responded</option>
            <option value="resolved">Resolved</option>
          </select>
          
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="facilities">Facilities</option>
            <option value="events">Events</option>
            <option value="booking">Booking</option>
            <option value="staff">Staff</option>
            <option value="suggestion">Suggestion</option>
            <option value="complaint">Complaint</option>
          </select>
          
          <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}>
            <option value="all">All Ratings</option>
            <option value="5">5 Stars ★★★★★</option>
            <option value="4">4+ Stars ★★★★☆</option>
            <option value="3">3+ Stars ★★★☆☆</option>
            <option value="2">2+ Stars ★★☆☆☆</option>
            <option value="1">1+ Star ★☆☆☆☆</option>
          </select>
          
          <button className="reset-btn" onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
            setCategoryFilter('all');
            setRatingFilter('all');
            setCurrentPage(1);
          }}>
            <i className="fas fa-undo-alt"></i> Reset
          </button>
        </div>
      </motion.div>

      {/* Results Info */}
      <div className="results-info">
        <span>Showing {currentFeedbacks.length} of {filteredFeedbacks.length} entries</span>
      </div>

      {/* Feedback Grid */}
      {currentFeedbacks.length === 0 ? (
        <motion.div 
          className="empty-state"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <i className="fas fa-inbox"></i>
          <h3>No feedback found</h3>
          <p>Try adjusting your search or filters</p>
        </motion.div>
      ) : (
        <motion.div 
          className="feedback-grid"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {currentFeedbacks.map((feedback, index) => {
            const statusConfig = getStatusConfig(feedback.status);
            const categoryConfig = getCategoryConfig(feedback.category);
            return (
              <motion.div 
                key={feedback.id} 
                className="feedback-card"
                variants={fadeInUp}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
              >
                <div className="card-header">
                  <div className="member-info">
                    <div className="member-avatar">
                      {feedback.member_name ? feedback.member_name.charAt(0).toUpperCase() : 'A'}
                    </div>
                    <div>
                      <h4>{feedback.member_name || 'Anonymous Member'}</h4>
                      <span className="member-email">{feedback.member_email}</span>
                    </div>
                  </div>
                  <div className="rating">
                    <div className="stars">{getRatingStars(feedback.rating)}</div>
                    <span className="rating-value">{feedback.rating}/5</span>
                  </div>
                </div>

                <div className="card-body">
                  <div className="category-badge" style={{ background: `${categoryConfig.color}15`, color: categoryConfig.color }}>
                    <i className={`fas ${categoryConfig.icon}`}></i>
                    {categoryConfig.label}
                  </div>
                  <p className="comment">{feedback.comment}</p>
                  <div className="date">
                    <i className="fas fa-calendar-alt"></i>
                    {formatDate(feedback.submitted_at)}
                  </div>
                </div>

                <div className="card-footer">
                  <div className="status-badge" style={{ background: statusConfig.bg, color: statusConfig.color }}>
                    <i className={`fas ${statusConfig.icon}`}></i>
                    {statusConfig.label}
                  </div>
                  <div className="actions">
                    <motion.button 
                      className="action-btn respond"
                      onClick={() => openResponseModal(feedback)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <i className="fas fa-reply"></i> Respond
                    </motion.button>
                    <motion.button 
                      className="action-btn delete"
                      onClick={() => handleDeleteFeedback(feedback.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </motion.button>
                  </div>
                </div>

                {feedback.admin_response && (
                  <div className="admin-response">
                    <div className="response-label">
                      <i className="fas fa-reply-all"></i> Admin Response
                    </div>
                    <p>{feedback.admin_response}</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            <i className="fas fa-chevron-right"></i>
          </button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      )}

      {/* Response Modal */}
      <AnimatePresence>
        {showResponseModal && selectedFeedback && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowResponseModal(false)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>
                  <i className="fas fa-reply-all"></i>
                  Respond to Feedback
                </h3>
                <button className="close-btn" onClick={() => setShowResponseModal(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="modal-body">
                <div className="feedback-details">
                  <div className="detail-row">
                    <strong>Member:</strong>
                    <span>{selectedFeedback.member_name || 'Anonymous'} ({selectedFeedback.member_email})</span>
                  </div>
                  <div className="detail-row">
                    <strong>Rating:</strong>
                    <span className="stars">{getRatingStars(selectedFeedback.rating)}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Category:</strong>
                    <span>{getCategoryConfig(selectedFeedback.category).label}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Comment:</strong>
                    <p className="feedback-comment">{selectedFeedback.comment}</p>
                  </div>
                </div>

                <div className="response-section">
                  <label>Status:</label>
                  <select 
                    value={selectedStatus} 
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="responded">Responded</option>
                    <option value="resolved">Resolved</option>
                  </select>

                  <label>Admin Response:</label>
                  <textarea
                    rows="5"
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response here... This will be visible to the member."
                  />

                  {selectedFeedback.admin_response && (
                    <div className="previous-response">
                      <strong>Previous Response:</strong>
                      <p>{selectedFeedback.admin_response}</p>
                      <small>Sent: {formatDate(selectedFeedback.responded_at)}</small>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="cancel-btn" onClick={() => setShowResponseModal(false)}>
                  Cancel
                </button>
                <button 
                  className="submit-btn" 
                  onClick={handleSubmitResponse}
                  disabled={updating}
                >
                  {updating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
                  {updating ? 'Sending...' : 'Send Response'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .admin-feedback-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 40px 24px;
          background: #f5f5f7;
          min-height: 100vh;
        }

        /* Header */
        .feedback-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 20px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .header-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #00843D, #00a54c);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px rgba(0,132,61,0.2);
        }

        .header-icon i {
          font-size: 28px;
          color: white;
        }

        .header-text h1 {
          margin: 0;
          font-size: 32px;
          font-weight: 700;
          color: #1d1d1f;
          letter-spacing: -0.5px;
        }

        .header-text p {
          margin: 4px 0 0;
          color: #6e6e73;
          font-size: 15px;
        }

        .refresh-btn {
          padding: 10px 24px;
          background: white;
          border: 1px solid #e5e5e7;
          border-radius: 40px;
          font-size: 14px;
          font-weight: 500;
          color: #1d1d1f;
          cursor: pointer;
          transition: all 0.2s;
        }

        .refresh-btn:hover {
          background: #f5f5f7;
          border-color: #00843D;
          color: #00843D;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
        }

        .stat-icon {
          width: 52px;
          height: 52px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        .stat-card.total .stat-icon {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .stat-card.pending .stat-icon {
          background: #fff3e0;
          color: #ed6c02;
        }

        .stat-card.responded .stat-icon {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .stat-card.resolved .stat-icon {
          background: #e0f2fe;
          color: #0284c7;
        }

        .stat-card.rating .stat-icon {
          background: #fef3c7;
          color: #d97706;
        }

        .stat-info {
          flex: 1;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1d1d1f;
          line-height: 1;
        }

        .stat-value .small {
          font-size: 14px;
          font-weight: 500;
          color: #6e6e73;
        }

        .stat-label {
          font-size: 13px;
          color: #6e6e73;
          margin-top: 6px;
        }

        /* Filters */
        .filters-section {
          background: white;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f5f5f7;
          border-radius: 14px;
          margin-bottom: 16px;
        }

        .search-wrapper i {
          color: #6e6e73;
          font-size: 16px;
        }

        .search-wrapper input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 15px;
          outline: none;
        }

        .filter-group {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .filter-group select {
          padding: 10px 16px;
          background: #f5f5f7;
          border: none;
          border-radius: 40px;
          font-size: 13px;
          color: #1d1d1f;
          cursor: pointer;
          outline: none;
        }

        .reset-btn {
          padding: 10px 20px;
          background: #f5f5f7;
          border: none;
          border-radius: 40px;
          font-size: 13px;
          color: #6e6e73;
          cursor: pointer;
          transition: all 0.2s;
        }

        .reset-btn:hover {
          background: #e5e5e7;
          color: #1d1d1f;
        }

        /* Results Info */
        .results-info {
          margin-bottom: 20px;
          font-size: 13px;
          color: #6e6e73;
        }

        /* Feedback Grid */
        .feedback-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
          margin-bottom: 40px;
        }

        .feedback-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          transition: all 0.3s;
        }

        .feedback-card:hover {
          box-shadow: 0 12px 32px rgba(0,0,0,0.1);
        }

        .card-header {
          padding: 20px;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .member-info {
          display: flex;
          gap: 12px;
        }

        .member-avatar {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #00843D, #00a54c);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 18px;
        }

        .member-info h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1d1d1f;
        }

        .member-email {
          font-size: 12px;
          color: #6e6e73;
        }

        .rating {
          text-align: right;
        }

        .stars {
          color: #fbbf24;
          font-size: 14px;
          letter-spacing: 2px;
        }

        .rating-value {
          font-size: 12px;
          color: #6e6e73;
          margin-left: 6px;
        }

        .card-body {
          padding: 20px;
        }

        .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 16px;
        }

        .comment {
          font-size: 14px;
          line-height: 1.6;
          color: #1d1d1f;
          margin: 0 0 16px;
        }

        .date {
          font-size: 12px;
          color: #6e6e73;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .card-footer {
          padding: 16px 20px;
          background: #fafafa;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
        }

        .actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.respond {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .action-btn.respond:hover {
          background: #c8e6c9;
        }

        .action-btn.delete {
          background: #ffebee;
          color: #c62828;
        }

        .action-btn.delete:hover {
          background: #ffcdd2;
        }

        .admin-response {
          padding: 16px 20px;
          background: #f0fdf4;
          border-top: 1px solid #d1fae5;
        }

        .response-label {
          font-size: 12px;
          font-weight: 600;
          color: #166534;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .admin-response p {
          margin: 0;
          font-size: 13px;
          color: #14532d;
          line-height: 1.5;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 80px 20px;
          background: white;
          border-radius: 20px;
        }

        .empty-state i {
          font-size: 64px;
          color: #d1d5db;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          margin: 0 0 8px;
          font-size: 20px;
          font-weight: 600;
          color: #1d1d1f;
        }

        .empty-state p {
          margin: 0;
          color: #6e6e73;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          padding: 20px;
          background: white;
          border-radius: 16px;
        }

        .pagination button {
          width: 40px;
          height: 40px;
          border: none;
          background: #f5f5f7;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          color: #1d1d1f;
        }

        .pagination button:hover:not(:disabled) {
          background: #00843D;
          color: white;
        }

        .pagination button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .page-info {
          padding: 0 16px;
          font-size: 14px;
          color: #6e6e73;
        }

        /* Loading */
        .admin-feedback-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 3px solid #e5e5e7;
          border-top-color: #00843D;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 560px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e5e7;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1d1d1f;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .close-btn {
          width: 36px;
          height: 36px;
          border: none;
          background: #f5f5f7;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #e5e5e7;
        }

        .modal-body {
          padding: 24px;
        }

        .feedback-details {
          background: #f8f9fa;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .detail-row {
          margin-bottom: 12px;
        }

        .detail-row strong {
          display: inline-block;
          width: 80px;
          color: #6e6e73;
          font-size: 13px;
        }

        .feedback-comment {
          margin-top: 12px;
          padding: 12px;
          background: white;
          border-radius: 12px;
          font-size: 14px;
          line-height: 1.6;
          color: #1d1d1f;
        }

        .response-section label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          color: #1d1d1f;
          font-size: 14px;
        }

        .status-select {
          width: 100%;
          padding: 12px;
          border: 1px solid #e5e5e7;
          border-radius: 12px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .response-section textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #e5e5e7;
          border-radius: 12px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
          margin-bottom: 20px;
        }

        .response-section textarea:focus {
          outline: none;
          border-color: #00843D;
        }

        .previous-response {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
        }

        .previous-response strong {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
        }

        .previous-response p {
          margin: 0 0 8px;
          font-size: 14px;
          color: #1d1d1f;
        }

        .previous-response small {
          font-size: 11px;
          color: #6e6e73;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e5e5e7;
        }

        .cancel-btn {
          padding: 10px 24px;
          background: #f5f5f7;
          border: none;
          border-radius: 40px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .submit-btn {
          padding: 10px 28px;
          background: #00843D;
          border: none;
          border-radius: 40px;
          font-size: 14px;
          font-weight: 600;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .submit-btn:hover:not(:disabled) {
          background: #006b31;
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .admin-feedback-container {
            padding: 20px 16px;
          }
          
          .feedback-grid {
            grid-template-columns: 1fr;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          
          .filter-group {
            flex-direction: column;
          }
          
          .filter-group select,
          .reset-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminFeedbackPanel;